"use client";

import { c as _c } from "react/compiler-runtime";
import { Button } from "@/components/ui/Button";
import { IconButton } from "@/components/ui/IconButton";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/Popover";
import { SegmentedControl, SegmentedControlItem } from "@/components/ui/SegmentedControl";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/Select";
import { Switch } from "@/components/ui/Switch";
import { Text } from "@/components/ui/Text";
import type { ListDisplayController } from "@/hooks/useListController";
import { ArrowDownWideNarrow, ArrowUpDown, LayoutList, Rows2, SlidersHorizontal } from "lucide-react";
import cn from "twsx";
function hasMultipleModes(display: ListDisplayController) {
  return display.config.supportsGrid;
}
function ListDisplaySection(t0) {
  const $ = _c(5);
  const {
    children,
    divided: t1
  } = t0;
  const divided = t1 === undefined ? false : t1;
  const t2 = divided && "border-t border-border-primary pt-2.25";
  let t3;
  if ($[0] !== t2) {
    t3 = cn("flex flex-col gap-1.75", t2);
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== children || $[3] !== t3) {
    t4 = <div className={t3}>{children}</div>;
    $[2] = children;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  return t4;
}
function ListDisplaySelect(t0) {
  const $ = _c(21);
  const {
    label,
    value,
    options,
    onChange,
    icon,
    disabled: t1
  } = t0;
  const disabled = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== label) {
    t2 = <Text tone="tertiary">{label}</Text>;
    $[0] = label;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <SelectValue />;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== icon) {
    t4 = <span className="flex min-w-0 items-center gap-1.5">{icon}{t3}</span>;
    $[3] = icon;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== label || $[6] !== t4) {
    t5 = <SelectTrigger size="sm" aria-label={label} className="w-32 shrink-0">{t4}</SelectTrigger>;
    $[5] = label;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== options) {
    t6 = options.map(_temp);
    $[8] = options;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t6) {
    t7 = <SelectContent align="end">{t6}</SelectContent>;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== disabled || $[13] !== onChange || $[14] !== t5 || $[15] !== t7 || $[16] !== value) {
    t8 = <Select value={value} onValueChange={onChange} disabled={disabled}>{t5}{t7}</Select>;
    $[12] = disabled;
    $[13] = onChange;
    $[14] = t5;
    $[15] = t7;
    $[16] = value;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t2 || $[19] !== t8) {
    t9 = <div className="flex items-center justify-between gap-3">{t2}{t8}</div>;
    $[18] = t2;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  return t9;
}
function _temp(option) {
  return <SelectItem key={option.key} value={option.key}>{option.label}</SelectItem>;
}
export function ListDisplayMenu(t0) {
  const $ = _c(90);
  const {
    display
  } = t0;
  const hasGrouping = display.config.groupingOptions.length > 0;
  const hasSubGrouping = display.config.subGroupingOptions.length > 0;
  const hasOrdering = display.config.orderingOptions.length > 0;
  const hasListOptions = display.config.listOptionToggles.length > 0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      key: "__none__",
      label: "No grouping"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== display.config.groupingOptions) {
    t2 = [t1, ...display.config.groupingOptions.map(_temp2)];
    $[1] = display.config.groupingOptions;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const groupingOptions = t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      key: "__none__",
      label: "No sub-grouping"
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== display.config.subGroupingOptions) {
    t4 = [t3, ...display.config.subGroupingOptions.map(_temp3)];
    $[4] = display.config.subGroupingOptions;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const subGroupingOptions = t4;
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      key: "__none__",
      label: "Manual"
    };
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== display.config.orderingOptions) {
    t6 = [t5, ...display.config.orderingOptions.map(_temp4)];
    $[7] = display.config.orderingOptions;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const orderingOptions = t6;
  let T0;
  let T1;
  let T2;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t20;
  let t7;
  let t8;
  let t9;
  if ($[9] !== display || $[10] !== groupingOptions || $[11] !== hasGrouping || $[12] !== hasListOptions || $[13] !== hasOrdering || $[14] !== hasSubGrouping || $[15] !== orderingOptions || $[16] !== subGroupingOptions) {
    let t21;
    if ($[34] !== display.state) {
      t21 = (left, right) => {
        const leftActive = display.state.visiblePropertyKeys.includes(left.key);
        const rightActive = display.state.visiblePropertyKeys.includes(right.key);
        if (leftActive === rightActive) {
          return 0;
        }
        return leftActive ? -1 : 1;
      };
      $[34] = display.state;
      $[35] = t21;
    } else {
      t21 = $[35];
    }
    const sortedPropertyOptions = [...display.config.propertyOptions].sort(t21);
    T2 = Popover;
    if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
      t20 = <PopoverTrigger asChild={true}><IconButton label="Display" variant="toolbar" size={32} type="button"><SlidersHorizontal /></IconButton></PopoverTrigger>;
      $[36] = t20;
    } else {
      t20 = $[36];
    }
    T1 = PopoverContent;
    t17 = "end";
    t18 = 8;
    t19 = "w-72";
    t11 = "flex flex-col gap-2.25";
    if ($[37] !== display) {
      t12 = hasMultipleModes(display) && <SegmentedControl aria-label="View"><SegmentedControlItem pressed={display.state.viewMode === "table"} onClick={() => display.setViewMode("table")}><LayoutList className="size-3.5" />List</SegmentedControlItem><SegmentedControlItem pressed={display.state.viewMode === "grid"} onClick={() => display.setViewMode("grid")}><Rows2 className="size-3.5" />Board</SegmentedControlItem></SegmentedControl>;
      $[37] = display;
      $[38] = t12;
    } else {
      t12 = $[38];
    }
    if ($[39] !== display || $[40] !== groupingOptions || $[41] !== hasGrouping) {
      t13 = hasGrouping && <ListDisplaySection divided={hasMultipleModes(display)}><ListDisplaySelect label="Grouping" value={display.state.groupBy ?? "__none__"} options={groupingOptions} onChange={value => display.setGroupBy(value === "__none__" ? null : value)} /></ListDisplaySection>;
      $[39] = display;
      $[40] = groupingOptions;
      $[41] = hasGrouping;
      $[42] = t13;
    } else {
      t13 = $[42];
    }
    if ($[43] !== display || $[44] !== hasGrouping || $[45] !== hasSubGrouping || $[46] !== subGroupingOptions) {
      t14 = hasSubGrouping && <ListDisplaySection divided={hasGrouping || hasMultipleModes(display)}><ListDisplaySelect label="Sub-grouping" value={display.state.subGroupBy ?? "__none__"} options={subGroupingOptions} disabled={!display.state.groupBy} onChange={value_0 => display.setSubGroupBy(value_0 === "__none__" ? null : value_0)} /></ListDisplaySection>;
      $[43] = display;
      $[44] = hasGrouping;
      $[45] = hasSubGrouping;
      $[46] = subGroupingOptions;
      $[47] = t14;
    } else {
      t14 = $[47];
    }
    if ($[48] !== display || $[49] !== hasGrouping || $[50] !== hasOrdering || $[51] !== hasSubGrouping || $[52] !== orderingOptions) {
      t15 = hasOrdering && <ListDisplaySection divided={hasGrouping || hasSubGrouping}><div className="flex items-center justify-between gap-3"><ListDisplaySelect label="Ordering" value={display.state.orderBy ?? "__none__"} options={orderingOptions} onChange={value_1 => display.setOrderBy(value_1 === "__none__" ? null : value_1)} icon={<ArrowDownWideNarrow className="size-3.5" />} /><IconButton label={`Direction ${display.state.orderDirection === "asc" ? "ascending" : "descending"}`} variant="secondary" size={28} type="button" onClick={display.toggleOrderDirection}><ArrowUpDown className={cn(display.state.orderDirection === "asc" && "rotate-180")} /></IconButton></div></ListDisplaySection>;
      $[48] = display;
      $[49] = hasGrouping;
      $[50] = hasOrdering;
      $[51] = hasSubGrouping;
      $[52] = orderingOptions;
      $[53] = t15;
    } else {
      t15 = $[53];
    }
    if ($[54] !== display || $[55] !== hasListOptions || $[56] !== hasOrdering) {
      t16 = hasListOptions && <ListDisplaySection divided={hasOrdering}><Text tone="tertiary">List options</Text><div className="flex flex-col gap-1.5">{display.config.listOptionToggles.map(option_2 => {
            const checked = display.state.listOptionState[option_2.key] ?? false;
            return <label key={option_2.key} className="inline-flex items-center justify-between gap-3 rounded-kbd p-1 hover:bg-fill-hover"><Text tone="secondary">{option_2.label}</Text><Switch checked={checked} onCheckedChange={nextChecked => display.setListOption(option_2.key, !!nextChecked)} /></label>;
          })}</div></ListDisplaySection>;
      $[54] = display;
      $[55] = hasListOptions;
      $[56] = hasOrdering;
      $[57] = t16;
    } else {
      t16 = $[57];
    }
    T0 = ListDisplaySection;
    t9 = hasOrdering || hasListOptions;
    if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <Text tone="tertiary">Display properties</Text>;
      $[58] = t10;
    } else {
      t10 = $[58];
    }
    t7 = "flex flex-wrap gap-1.5";
    let t22;
    if ($[59] !== display) {
      t22 = property => {
        const active = display.state.visiblePropertyKeys.includes(property.key);
        return <SegmentedControlItem key={property.key} pressed={active} onClick={() => display.toggleProperty(property.key)}>{property.label}</SegmentedControlItem>;
      };
      $[59] = display;
      $[60] = t22;
    } else {
      t22 = $[60];
    }
    t8 = sortedPropertyOptions.map(t22);
    $[9] = display;
    $[10] = groupingOptions;
    $[11] = hasGrouping;
    $[12] = hasListOptions;
    $[13] = hasOrdering;
    $[14] = hasSubGrouping;
    $[15] = orderingOptions;
    $[16] = subGroupingOptions;
    $[17] = T0;
    $[18] = T1;
    $[19] = T2;
    $[20] = t10;
    $[21] = t11;
    $[22] = t12;
    $[23] = t13;
    $[24] = t14;
    $[25] = t15;
    $[26] = t16;
    $[27] = t17;
    $[28] = t18;
    $[29] = t19;
    $[30] = t20;
    $[31] = t7;
    $[32] = t8;
    $[33] = t9;
  } else {
    T0 = $[17];
    T1 = $[18];
    T2 = $[19];
    t10 = $[20];
    t11 = $[21];
    t12 = $[22];
    t13 = $[23];
    t14 = $[24];
    t15 = $[25];
    t16 = $[26];
    t17 = $[27];
    t18 = $[28];
    t19 = $[29];
    t20 = $[30];
    t7 = $[31];
    t8 = $[32];
    t9 = $[33];
  }
  let t21;
  if ($[61] !== t7 || $[62] !== t8) {
    t21 = <div className={t7}>{t8}</div>;
    $[61] = t7;
    $[62] = t8;
    $[63] = t21;
  } else {
    t21 = $[63];
  }
  let t22;
  if ($[64] !== T0 || $[65] !== t10 || $[66] !== t21 || $[67] !== t9) {
    t22 = <T0 divided={t9}>{t10}{t21}</T0>;
    $[64] = T0;
    $[65] = t10;
    $[66] = t21;
    $[67] = t9;
    $[68] = t22;
  } else {
    t22 = $[68];
  }
  let t23;
  if ($[69] !== display.reset) {
    t23 = <div className="border-t border-border-primary pt-2.25"><Button variant="ghost" size="sm" type="button" onClick={display.reset}>Reset display</Button></div>;
    $[69] = display.reset;
    $[70] = t23;
  } else {
    t23 = $[70];
  }
  let t24;
  if ($[71] !== t11 || $[72] !== t12 || $[73] !== t13 || $[74] !== t14 || $[75] !== t15 || $[76] !== t16 || $[77] !== t22 || $[78] !== t23) {
    t24 = <div className={t11}>{t12}{t13}{t14}{t15}{t16}{t22}{t23}</div>;
    $[71] = t11;
    $[72] = t12;
    $[73] = t13;
    $[74] = t14;
    $[75] = t15;
    $[76] = t16;
    $[77] = t22;
    $[78] = t23;
    $[79] = t24;
  } else {
    t24 = $[79];
  }
  let t25;
  if ($[80] !== T1 || $[81] !== t17 || $[82] !== t18 || $[83] !== t19 || $[84] !== t24) {
    t25 = <T1 align={t17} sideOffset={t18} className={t19}>{t24}</T1>;
    $[80] = T1;
    $[81] = t17;
    $[82] = t18;
    $[83] = t19;
    $[84] = t24;
    $[85] = t25;
  } else {
    t25 = $[85];
  }
  let t26;
  if ($[86] !== T2 || $[87] !== t20 || $[88] !== t25) {
    t26 = <T2>{t20}{t25}</T2>;
    $[86] = T2;
    $[87] = t20;
    $[88] = t25;
    $[89] = t26;
  } else {
    t26 = $[89];
  }
  return t26;
}
function _temp4(option_1) {
  return {
    key: option_1.key,
    label: option_1.label
  };
}
function _temp3(option_0) {
  return {
    key: option_0.key,
    label: option_0.label
  };
}
function _temp2(option) {
  return {
    key: option.key,
    label: option.label
  };
}